import { useState } from "react";
import api from "../api";
import * as XLSX from "xlsx";

const ORANGE = "#FF6B2B";
const fmtBR = (v) => `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;
const fmtData = (d) => { if(!d) return "—"; const raw = new Date(d); const dt = new Date(raw.getTime() + raw.getTimezoneOffset()*60000); return dt.toLocaleDateString("pt-BR"); };

export default function RelatorioFinanceiro({ theme, categorias = [], contas = [], clientes = [] }) {
  const hoje = new Date();
  const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0,10);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().slice(0,10);

  const [filtros, setFiltros] = useState({ de: ini, ate: fim, campoData: "dataEmissao", tipo: "", status: "", categoria: "", banco: "", cliente: "" });
  const [dados, setDados] = useState(null);
  const [load, setLoad] = useState(false);
  const [buscaCliente, setBuscaCliente] = useState("");
  const [listaClienteAberta, setListaClienteAberta] = useState(false);

  const set = (k,v) => setFiltros(f => ({ ...f, [k]: v }));

  const gerar = async () => {
    setLoad(true);
    try {
      const params = new URLSearchParams(Object.entries(filtros).filter(([,v]) => v)).toString();
      const { data } = await api.get(`/financeiro/relatorio?${params}`);
      setDados(data);
    } catch (e) { alert(e.response?.data?.erro || "Erro ao gerar relatório"); }
    finally { setLoad(false); }
  };

  const exportarExcel = () => {
    if (!dados) return;
    const wb = XLSX.utils.book_new();

    // Aba Resumo
    const resumo = [
      ["Relatório Financeiro"],
      ["Período", `${fmtData(filtros.de)} a ${fmtData(filtros.ate)}`],
      ["Filtro de data", filtros.campoData === "dataPagamento" ? "Pagamento" : "Emissão"],
      [],
      ["Total Entradas", dados.totais.entradas],
      ["Total Saídas", dados.totais.saidas],
      ["Recebido", dados.totais.recebido],
      ["Pago", dados.totais.pago],
      ["Lucro Líquido", dados.totais.lucro],
      ["Qtd. Lançamentos", dados.totais.count],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumo), "Resumo");

    // Aba Lançamentos
    const linhas = dados.lista.map(l => ({
      Descrição: l.descricao, Tipo: l.tipo, Categoria: l.categoria || "", Banco: l.banco || "",
      "Nº Nota": l.numeroNota || "", Cliente: l.clienteNome || "",
      Emissão: fmtData(l.dataEmissao), Vencimento: fmtData(l.dataVencimento), Pagamento: fmtData(l.dataPagamento),
      Valor: l.valor || 0, Status: l.status,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), "Lançamentos");

    // Aba Por Categoria
    const cats = dados.porCategoria.map(c => ({ Categoria: c.nome, Valor: c.valor }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cats), "Por Categoria");

    // Aba Por Banco
    const bancos = dados.porBanco.map(b => ({ Banco: b.nome, Entradas: b.entradas, Saídas: b.saidas, Total: b.valor }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bancos), "Por Banco");

    XLSX.writeFile(wb, `relatorio-financeiro-${filtros.de}-a-${filtros.ate}.xlsx`);
  };

  const inp = { background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"8px 10px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name };
  const lbl = { fontSize:11, color:theme.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:".04em", marginBottom:4, display:"block" };

  return (
    <div style={{ padding:"4px 0" }}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:14, padding:20, marginBottom:18 }}>
        <div style={{ fontSize:14, fontWeight:700, color:theme.text, marginBottom:16 }}>🔍 Filtros do relatório</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:14 }}>
          <div><label style={lbl}>De</label><input type="date" value={filtros.de} onChange={e=>set("de",e.target.value)} style={{ ...inp, width:"100%", boxSizing:"border-box" }} /></div>
          <div><label style={lbl}>Até</label><input type="date" value={filtros.ate} onChange={e=>set("ate",e.target.value)} style={{ ...inp, width:"100%", boxSizing:"border-box" }} /></div>
          <div><label style={lbl}>Filtrar data por</label><select value={filtros.campoData} onChange={e=>set("campoData",e.target.value)} style={{ ...inp, width:"100%", appearance:"auto" }}><option value="dataEmissao">Emissão</option><option value="dataPagamento">Pagamento</option></select></div>
          <div><label style={lbl}>Tipo</label><select value={filtros.tipo} onChange={e=>set("tipo",e.target.value)} style={{ ...inp, width:"100%", appearance:"auto" }}><option value="">Todos</option><option value="entrada">Entradas</option><option value="saida">Saídas</option></select></div>
          <div><label style={lbl}>Status</label><select value={filtros.status} onChange={e=>set("status",e.target.value)} style={{ ...inp, width:"100%", appearance:"auto" }}><option value="">Todos</option><option value="pendente">Pendente</option><option value="pago">Pago</option><option value="vencido">Vencido</option><option value="cancelado">Cancelado</option></select></div>
          <div><label style={lbl}>Categoria</label><select value={filtros.categoria} onChange={e=>set("categoria",e.target.value)} style={{ ...inp, width:"100%", appearance:"auto" }}><option value="">Todas</option>{categorias.map(c=><option key={c._id} value={c.nome}>{c.nome}</option>)}</select></div>
          <div><label style={lbl}>Banco</label><select value={filtros.banco} onChange={e=>set("banco",e.target.value)} style={{ ...inp, width:"100%", appearance:"auto" }}><option value="">Todos</option>{contas.map(c=><option key={c._id} value={c.nome}>{c.nome}</option>)}</select></div>
          <div style={{ position:"relative" }}>
            <label style={lbl}>Cliente</label>
            <input value={filtros.cliente || buscaCliente} placeholder="Buscar cliente..." style={{ ...inp, width:"100%", boxSizing:"border-box" }}
              onChange={e=>{ setBuscaCliente(e.target.value); set("cliente",""); setListaClienteAberta(true); }}
              onFocus={()=>setListaClienteAberta(true)}
              onBlur={()=>setTimeout(()=>setListaClienteAberta(false),180)}/>
            {filtros.cliente && <span onClick={()=>{ set("cliente",""); setBuscaCliente(""); }} style={{ position:"absolute", right:10, top:32, cursor:"pointer", color:theme.textMuted, fontSize:14 }}>×</span>}
            {listaClienteAberta && (buscaCliente.trim()||!filtros.cliente) && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, marginTop:2, maxHeight:200, overflowY:"auto", zIndex:50, boxShadow:"0 8px 24px rgba(0,0,0,.12)" }}>
                {clientes.filter(n=>n.toLowerCase().includes(buscaCliente.toLowerCase())).slice(0,40).map((nome,i)=>(
                  <div key={i} onMouseDown={()=>{ set("cliente",nome); setBuscaCliente(""); setListaClienteAberta(false); }}
                    style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, color:theme.text, borderBottom:`1px solid ${theme.border}` }}
                    onMouseEnter={e=>e.currentTarget.style.background=theme.bgInput} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    {nome}
                  </div>
                ))}
                {clientes.filter(n=>n.toLowerCase().includes(buscaCliente.toLowerCase())).length===0 && (
                  <div style={{ padding:"10px 12px", fontSize:12, color:theme.textMuted }}>Nenhum cliente encontrado.</div>
                )}
              </div>
            )}
          </div>
        </div>
        <button onClick={gerar} disabled={load} style={{ background:ORANGE, border:"none", borderRadius:10, padding:"10px 24px", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:load?.6:1 }}>{load?"Gerando...":"📊 Gerar relatório"}</button>
      </div>

      {!dados && <div style={{ padding:40, textAlign:"center", color:theme.textMuted, fontSize:13 }}>Configure os filtros e clique em "Gerar relatório".</div>}

      {dados && (
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginBottom:16 }}>
            <button onClick={exportarExcel} style={{ background:"#10b981", border:"none", borderRadius:10, padding:"9px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>📗 Exportar Excel</button>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12, marginBottom:18 }}>
            {[["Entradas",dados.totais.entradas,"#10b981"],["Saídas",dados.totais.saidas,"#ef4444"],["Recebido",dados.totais.recebido,"#0ea5e9"],["Pago",dados.totais.pago,"#f59e0b"],["Lucro",dados.totais.lucro,dados.totais.lucro>=0?"#10b981":"#ef4444"]].map(([lb,vl,cor],i)=>(
              <div key={i} style={{ background:`${cor}12`, border:`1px solid ${cor}33`, borderRadius:12, padding:"14px 16px" }}>
                <div style={{ fontSize:11, color:theme.textMuted, fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>{lb}</div>
                <div style={{ fontSize:18, fontWeight:800, color:cor }}>{fmtBR(vl)}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:18 }}>
            <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:14, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:12 }}>Por categoria</div>
              {dados.porCategoria.map((c,i)=>(
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${theme.border}`, fontSize:13, color:theme.text }}><span>{c.nome}</span><span style={{ fontWeight:700 }}>{fmtBR(c.valor)}</span></div>
              ))}
              {dados.porCategoria.length===0 && <div style={{ color:theme.textMuted, fontSize:12 }}>Sem dados.</div>}
            </div>
            <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:14, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:12 }}>Por banco</div>
              {dados.porBanco.map((b,i)=>(
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${theme.border}`, fontSize:13, color:theme.text }}><span>{b.nome}</span><span style={{ fontWeight:700 }}>{fmtBR(b.valor)}</span></div>
              ))}
              {dados.porBanco.length===0 && <div style={{ color:theme.textMuted, fontSize:12 }}>Sem dados.</div>}
            </div>
          </div>

          <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:14, padding:18, overflowX:"auto" }}>
            <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:12 }}>Lançamentos ({dados.lista.length})</div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:theme.bgInput }}>
                  {["Descrição","Categoria","Banco","Emissão","Pagamento","Valor","Status"].map((h,i)=>(
                    <th key={i} style={{ padding:"8px 10px", textAlign:i>=5?"right":"left", color:theme.textMuted, fontWeight:600, textTransform:"uppercase", fontSize:10, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.lista.map((l,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${theme.border}` }}>
                    <td style={{ padding:"8px 10px", color:theme.text }}>{l.descricao}</td>
                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{l.categoria||"—"}</td>
                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{l.banco||"—"}</td>
                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{fmtData(l.dataEmissao)}</td>
                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{fmtData(l.dataPagamento)}</td>
                    <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:l.tipo==="entrada"?"#10b981":"#ef4444" }}>{fmtBR(l.valor)}</td>
                    <td style={{ padding:"8px 10px", textAlign:"right", color:theme.textMuted }}>{l.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
